/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  HeaderAndLocation component
 */
/** @jsx jsx */
import { chakra, useColorModeValue } from '@chakra-ui/react'
import { jsx } from '@emotion/react'
import { isDetailPageNew } from '@zcode-ui/web-zumper-global-state'
import { VerifiedIcon } from '@zcode-ui/web-zumper-ui'
import { checkAddressHidden } from '@zcode/nr/es/shared/utils/address'
import type { Listable } from '@zcode/state-type'
import { capitalize, isTruthy } from '@zcode/utils'
import React from 'react'
import { useSelector } from 'react-redux'
import type { getPropertyTypeText } from '../../../../../../modules/router/selectors/index'
import { DetailPageLink } from '../../../../../detail/DetailPageLink/DetailPageLink'
import { AmenitiesLine } from '../AmenitiesLine/AmenitiesLine'
import { PaidPhone } from '../PaidPhone/PaidPhone'
import * as styles from './style'

interface Props {
  'aria-hidden'?: boolean
  entity: Listable
  isBuilding: boolean
  isLargeCard?: boolean
  isOverlay?: boolean
  onPaidPhoneClick: (e: React.SyntheticEvent) => void
  paidPhone?: string | null | undefined
  propertyTypeText: ReturnType<typeof getPropertyTypeText>
  verifiedTooltipTestID?: string
}

export const HeaderAndLocation = ({
  'aria-hidden': ariaHidden = false,
  entity,
  isBuilding,
  isLargeCard,
  isOverlay,
  onPaidPhoneClick,
  paidPhone,
  propertyTypeText,
  verifiedTooltipTestID,
}: Props) => {
  const {
    city,
    displayableAddress,
    headerText,
    isDirectDeal,
    neighborhoodName,
    state,
    zipcode,
    bedroomText,
    bathroomText,
  } = entity
  const cityAndHood = [neighborhoodName, city].filter(isTruthy).join(' · ')
  const displayableZipcode = zipcode == null ? '' : ` ${zipcode}`
  const displayableCityStateZip = `${[city, state]
    .filter(isTruthy)
    .join(', ')}${displayableZipcode}`

  let fullAddress = displayableAddress
    ? `${displayableAddress}, ${displayableCityStateZip}`
    : displayableCityStateZip

  if (checkAddressHidden(displayableAddress)) {
    const prefix = bathroomText ? `${bedroomText} & ${bathroomText}` : bedroomText

    fullAddress = prefix ? `${prefix} in ${displayableCityStateZip}` : displayableCityStateZip
  }

  const excludedPropertyTypes = ['apartments', 'rentals', 'monthly rentals', 'vacation rentals']
  const headerHasAddress = fullAddress.includes(headerText)
  const ListingHeaderWrapper = headerHasAddress ? 'address' : 'div'
  const addressColor = useColorModeValue('textLightest', 'gray.100')
  const addressColorHover = useColorModeValue('textLight', 'gray.100')
  const _isDetailPageNew = useSelector(isDetailPageNew)

  if (isOverlay) {
    return (
      <ListingHeaderWrapper css={styles.header}>
        <chakra.span<'span'>
          aria-hidden={ariaHidden}
          className="font-bold hover:text-gray-800 leading-[120%] mr-3 not-italic overflow-hidden text-[15px] text-gray-950 text-ellipsis whitespace-nowrap"
        >
          {cityAndHood}
        </chakra.span>
      </ListingHeaderWrapper>
    )
  }

  const addressChild = (
    <>
      {isBuilding && fullAddress}
      {!isBuilding && cityAndHood}
    </>
  )
  const address = (
    <address css={[styles.locationText, paidPhone && styles.hide]}>
      <chakra.span<'span'>
        _hover={{ color: addressColorHover }}
        color={addressColor}
        css={styles.address}
      >
        {addressChild}
      </chakra.span>

      <span css={styles.hide}>{zipcode ? `, ${zipcode}` : ''}</span>
      {propertyTypeText && !excludedPropertyTypes.includes(propertyTypeText) && (
        <h2 css={[styles.locationText, styles.propertyTypeText]}>
          {`· ${capitalize(propertyTypeText.slice(0, -1))} for Rent`}
        </h2>
      )}
    </address>
  )

  // hide the <a> tag for noIndex detail pages https://zumper.atlassian.net/browse/ZUM-54976

  return (
    <React.Fragment>
      <ListingHeaderWrapper css={styles.header}>
        <DetailPageLink
          aria-hidden={ariaHidden}
          className={`${isDirectDeal ? 'font-bold' : 'font-extrabold'} hover:no-underline hover:text-gray-800 leading-[120%] mr-3 not-italic overflow-hidden text-[15px] text-gray-950 text-ellipsis whitespace-nowrap`}
          entity={entity}
          tabIndex={ariaHidden ? -1 : 0}
        >
          {isBuilding ? headerText : fullAddress}
        </DetailPageLink>

        <VerifiedIcon
          data-testid={verifiedTooltipTestID}
          isDirectDeal={entity.isDirectDeal}
          isNewDetailPage={_isDetailPageNew}
          size={15}
        />
      </ListingHeaderWrapper>

      {address}
      {isLargeCard && <AmenitiesLine listable={entity} />}
      {paidPhone && <PaidPhone onClick={onPaidPhoneClick} paidPhone={paidPhone} />}
    </React.Fragment>
  )
}
